import React, { useState } from "react";
import { astryx, DataTable, SectionHeader, ScrollText } from "@shadowcue/ui";
import type { IUser } from "../../types/iuser";
import useScriptTemplates, {
  type IScriptTemplateSummary,
} from "../../hooks/useScriptTemplates";

interface ScriptTemplatesTableProps {
  currentUser: IUser;
}

const TEMPLATES_PER_PAGE = 5;

/**
 * The organization's script templates, newest first, so the dashboard shows
 * what screens exist — not just what's been run. Only admins can open a
 * template, so members get the same rows without links.
 */
const ScriptTemplatesTable = ({ currentUser }: ScriptTemplatesTableProps) => {
  const [page, setPage] = useState(1);
  const { templates, totalPages, loading, error } = useScriptTemplates(
    currentUser.authToken,
    TEMPLATES_PER_PAGE,
    page,
  );

  const templatePath = (template: IScriptTemplateSummary) =>
    `/admin/script_templates/${template.id}`;

  const columns: astryx.TableColumn<IScriptTemplateSummary>[] = [
    {
      key: "name",
      header: "Name",
      width: astryx.proportional(2),
      renderCell: (template) =>
        currentUser.admin ? (
          <astryx.Link href={templatePath(template)}>{template.name}</astryx.Link>
        ) : (
          template.name
        ),
    },
    {
      key: "scriptTemplateSectionsCount",
      header: "Sections",
      width: astryx.pixel(110),
      align: "end",
    },
    {
      key: "scriptTemplateQuestionsCount",
      header: "Questions",
      width: astryx.pixel(110),
      align: "end",
    },
    {
      key: "createdAt",
      header: "Created",
      width: astryx.pixel(140),
      align: "end",
      renderCell: (template) => (
        <astryx.Timestamp value={template.createdAt} format="date" hasTooltip={false} />
      ),
    },
  ];

  return (
    <astryx.Card variant="default">
      <astryx.VStack gap={4}>
        <SectionHeader
          title="Script templates"
          action={
            currentUser.admin && templates.length > 0 ? (
              <astryx.Link href="/admin/script_templates">View all</astryx.Link>
            ) : undefined
          }
        />
        {error ? (
          <astryx.Banner
            status="error"
            title="Couldn't load your script templates"
            container="card"
          />
        ) : (
          <DataTable
            loading={loading}
            data={templates}
            columns={columns}
            idKey="id"
            pagination={{ page, totalPages, onChange: setPage }}
            emptyState={{
              icon: <astryx.Icon icon={ScrollText} />,
              headline: "No script templates yet",
              subtext: currentUser.admin
                ? "Create one to launch your first AI-guided screen."
                : "An admin creates these for your organization.",
              action: currentUser.admin ? (
                <astryx.Button
                  label="New template"
                  variant="secondary"
                  href="/admin/script_templates/new"
                />
              ) : undefined,
            }}
          />
        )}
      </astryx.VStack>
    </astryx.Card>
  );
};

export default ScriptTemplatesTable;
